import Modal from "@ts-digital/vrc/es/components/modal";
import { find, findIndex } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf/dist/esm/entry.webpack";
import { API_URL } from "../../../config";
import DocumentDto from "../../../PaymentContainerClient/dtos/DocumentDto";
import PaymentRequestDto from "../../../PaymentContainerClient/dtos/PaymentRequestDto";
import getFormattedMessageWithScope from "../../../utils/getFormattedMessageWithScope";
import { SpinnerIcon } from "../SpinnerIcon";
import { ToastVariant } from "../Toast/Toast";
import { useToast } from "../Toast/ToastProvider";
import { useWindowDimensions } from "../WindowDimensionsProvider";
import { DocumentsActionsToolbar } from "./DocumentsActionsToolbar";
import "./style.css";
import { Toolbar } from "./Toolbar";

const fm = getFormattedMessageWithScope("components.DocumentsPreviewer");
const fme = getFormattedMessageWithScope("errors");

interface DocumentsPreviewerProps {
    isOpen: boolean;
    onClose: Function;
    documents: DocumentDto[];
    documentsAvailableForTheUser: DocumentDto[];
    onDownload: Function;
    isDownloading: boolean;
    paymentRequest: PaymentRequestDto;
    documentIdDownloading?: string;
    defaultSelectedDocument?: DocumentDto;
}

export const DocumentsPreviewer = ({
    isOpen,
    onClose,
    documents,
    documentsAvailableForTheUser,
    onDownload,
    isDownloading,
    paymentRequest,
    documentIdDownloading,
    defaultSelectedDocument,
}: DocumentsPreviewerProps) => {
    const { width } = useWindowDimensions();
    const { addToast } = useToast();
    const [selectedDocument, setSelectedDocument] = useState<
        DocumentDto | undefined
    >(documentsAvailableForTheUser[0]);
    const [numberOfPages, setNumberOfPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const defaultScale =
        width < 600 ? 0.7 : width < 1300 ? 1 : width < 1650 ? 1.3 : 1.6;
    const [scale, setScale] = useState(defaultScale);
    const [hasErrorLoadingPreview, setHasErrorLoadingPreview] = useState(false);
    const [isPreviewLoaded, setIsPreviewLoaded] = useState(false);
    const canChangeDocument = documentsAvailableForTheUser.length > 1;

    const resetPreviewSettingsToDefault = useCallback(
        (nextDocument?: DocumentDto) => {
            if (
                selectedDocument &&
                nextDocument &&
                selectedDocument.documentId !== nextDocument.documentId
            ) {
                setHasErrorLoadingPreview(false);
                setCurrentPage(0);
                setIsPreviewLoaded(false);
                setScale(defaultScale);
            }
        },
        [defaultScale, selectedDocument]
    );

    const changeSelectedDocumentById = useCallback(
        (docId: string) => {
            const nextDocument = find(documentsAvailableForTheUser, {
                documentId: docId,
            });
            resetPreviewSettingsToDefault(nextDocument);
            setSelectedDocument(nextDocument);
        },
        [documentsAvailableForTheUser, resetPreviewSettingsToDefault]
    );

    function usePreviousPropIsOpen(value: boolean) {
        const ref = useRef<boolean>(false);
        useEffect(() => {
            ref.current = value;
        });
        return ref.current;
    }

    const previousPropIsOpen = usePreviousPropIsOpen(isOpen);

    useEffect(() => {
        if (defaultSelectedDocument && !previousPropIsOpen) {
            changeSelectedDocumentById(defaultSelectedDocument.documentId);
        }
    }, [
        defaultSelectedDocument,
        changeSelectedDocumentById,
        previousPropIsOpen,
    ]);

    function changeSelectedDocument(position: number) {
        if (canChangeDocument) {
            const index = findIndex(documentsAvailableForTheUser, {
                documentId: selectedDocument!.documentId,
            });
            let newSelectedDocument;
            if (position === -1) {
                newSelectedDocument =
                    index > 0
                        ? documentsAvailableForTheUser[index - 1]
                        : documentsAvailableForTheUser[
                              documentsAvailableForTheUser.length - 1
                          ];
            } else {
                newSelectedDocument =
                    index < documentsAvailableForTheUser.length - 1
                        ? documentsAvailableForTheUser[index + 1]
                        : documentsAvailableForTheUser[0];
            }
            resetPreviewSettingsToDefault(newSelectedDocument);
            setSelectedDocument(newSelectedDocument);
        }
    }

    function onLoadPreviewSuccess(pdf: any) {
        setNumberOfPages(pdf.numPages);
        setIsPreviewLoaded(true);
    }

    function onLoadPreviewError() {
        setHasErrorLoadingPreview(true);
        addToast(fme("errorGettingDocumentPreview"), {
            variant: ToastVariant.Error,
            autoDismiss: false,
        });
    }

    function renderContent() {
        return (
            <div className="c-DocumentsPreviewer-content">
                <DocumentsActionsToolbar
                    canChangeDocument={canChangeDocument}
                    onChangeSelectedDocument={changeSelectedDocument}
                    onChangeSelectedDocumentById={changeSelectedDocumentById}
                    onDownload={onDownload}
                    isDownloading={isDownloading}
                    documentIdDownloading={documentIdDownloading}
                    selectedDocument={selectedDocument}
                    paymentRequest={paymentRequest}
                    documents={documents}
                />
                <div className="c-DocumentsPreviewer-preview-container">
                    {isOpen ? (
                        <div className="c-DocumentsPreviewer-Document-container">
                            <Document
                                file={`${API_URL}api/v1/public/paymentRequest/${
                                    paymentRequest.token
                                }/documents/${
                                    selectedDocument!.documentId
                                }/download`}
                                loading={
                                    <SpinnerIcon className="c-DocumentsPreviewer-preview-container-spinner-icon" />
                                }
                                onLoadSuccess={onLoadPreviewSuccess}
                                onLoadError={onLoadPreviewError}
                                error={fm("errorLoadingPreview")}
                                className="c-DocumentsPreviewer-Document"
                            >
                                <Page
                                    className="c-DocumentsPreviewer-Document-Page"
                                    pageIndex={currentPage}
                                    scale={scale}
                                    loading={
                                        <SpinnerIcon className="c-DocumentsPreviewer-preview-container-spinner-icon" />
                                    }
                                />
                            </Document>
                        </div>
                    ) : undefined}
                </div>
                {!hasErrorLoadingPreview && isPreviewLoaded && (
                    <Toolbar
                        currentPage={currentPage}
                        onChangeCurrentPage={setCurrentPage}
                        numberOfPages={numberOfPages}
                        scale={scale}
                        onChangeScale={setScale}
                    />
                )}
            </div>
        );
    }

    return width > 1000 ? (
        renderContent()
    ) : (
        <Modal
            visible={isOpen}
            onClose={onClose}
            title={
                <span className="c-DocumentsPreviewer-modal-title">
                    {fm("modalTitle")}
                </span>
            }
            className="c-DocumentsPreviewer-modal"
        >
            {renderContent()}
        </Modal>
    );
};
